import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Container,
  Grid,
} from "@mui/material";
import {
  Plus,
  ChevronRight,
  MoreVertical,
  Settings,
  Trash2,
  AlertCircle,
  Building,
  Briefcase,
  Users,
  TrendingUp,
} from "lucide-react";
import CommonLayout from "../../components/CommonLayout";
import { config } from "../../config";
import { useDispatch, useSelector } from "react-redux";
import SessionService from "../../services/Session";
import queryString from "query-string";
import { setUser } from "../../redux/actions/user";
import { logIssueNetworkRequestError } from "../../utils/Loggers";
import { homeRoutePath, settingsPath } from "../Router";
import { Project } from "../../interfaces/Project";
import SearchAndFilters from "../../components/SearchAndFilters";
import { SearchState } from "../../interfaces/Search";
import { filterConfigs } from "../../components/SearchAndFilters/config";
import ProjectService from "../../services/Project";
import { InitialSearchStates } from "../../components/SearchAndFilters/searchStates";
import { setProject } from "../../redux/actions/project";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import SearchContentContainer from "../../components/SearchContentContainer";
import { StyledButton } from "./config";
import { displayToastMessage } from "../../utils/Toasts";
import NewProjectDialog from "./NewProjectDialog";
import SubscriptionLimitDialog from "../../components/SubscriptionLimitDialog";
import { useSubscriptionLimit } from "../../hooks/useSubscriptionLimit";

interface ProjectsListProps {
  projects: any[];
  onMenuClick: (event: React.MouseEvent<HTMLElement>, project: any) => void;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const ProjectCard = ({ project, onMenuClick, index }: any) => {
  const navigate = useNavigate();
  const { basicCompanyInfo = {} } = project || {};

  // Helper function to handle undefined/null values
  const getDisplayValue = (value: any, defaultValue = "Not specified") => {
    if (value === undefined || value === null || value === "") {
      return defaultValue;
    }
    return value;
  };

  // Map industry sectors to icons
  const getIndustryIcon = (industry: string) => {
    const lowercased = (industry || "").toLowerCase();
    if (
      lowercased.includes("tech") ||
      lowercased.includes("software") ||
      lowercased.includes("saas")
    ) {
      return <TrendingUp className="w-4 h-4 text-blue-500" />;
    } else if (lowercased.includes("finance") || lowercased.includes("bank")) {
      return <Briefcase className="w-4 h-4 text-green-500" />;
    } else if (
      lowercased.includes("service") ||
      lowercased.includes("consult")
    ) {
      return <Users className="w-4 h-4 text-amber-500" />;
    } else {
      return <Building className="w-4 h-4 text-gray-500" />;
    }
  };

  const keyMetrics = [
    {
      label: "Company",
      value: getDisplayValue(basicCompanyInfo.companyName),
    },
    {
      label: "Industry",
      value: getDisplayValue(basicCompanyInfo.industrySector),
    },
    {
      label: "Size",
      value: basicCompanyInfo.companySize
        ? `${basicCompanyInfo.companySize} employee(s)`
        : "Size not specified",
    },
    {
      label: "Stage",
      value: getDisplayValue(basicCompanyInfo.currentGrowthStage),
    },
  ];

  // Only render if we have a project object
  if (!project) {
    return null;
  }

  // Generate a subtle background color based on industry
  const getBgColor = () => {
    const industry = (basicCompanyInfo.industrySector || "").toLowerCase();
    if (
      industry.includes("tech") ||
      industry.includes("software") ||
      industry.includes("saas")
    ) {
      return "bg-gradient-to-br from-blue-50 to-white";
    } else if (industry.includes("finance") || industry.includes("bank")) {
      return "bg-gradient-to-br from-green-50 to-white";
    } else if (industry.includes("service") || industry.includes("consult")) {
      return "bg-gradient-to-br from-amber-50 to-white";
    } else {
      return "bg-gradient-to-br from-gray-50 to-white";
    }
  };

  return (
    <motion.div
      className={`h-full rounded-lg bg-white border border-gray-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <div className={`p-6 h-full flex flex-col ${getBgColor()}`}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <Typography
              variant="h6"
              className="mb-1 line-clamp-1 font-semibold"
            >
              {getDisplayValue(
                basicCompanyInfo.companyName,
                "Untitled Project"
              )}
            </Typography>
            <div className="flex items-center text-gray-600 space-x-1">
              {getIndustryIcon(basicCompanyInfo.industrySector)}
              <Typography variant="body2" className="line-clamp-1">
                {getDisplayValue(
                  basicCompanyInfo.industrySector,
                  "Industry not specified"
                )}
              </Typography>
            </div>
          </div>
          <IconButton
            size="small"
            onClick={(e) => onMenuClick(e, project)}
            className="text-gray-500 bg-white/80 backdrop-blur-sm hover:bg-gray-100 shadow-sm"
          >
            <MoreVertical className="w-5 h-5" />
          </IconButton>
        </div>

        <div className="space-y-3 mb-6 bg-white/70 backdrop-blur-sm p-4 rounded-lg border border-gray-100 flex-grow">
          {keyMetrics.map((metric) => (
            <div
              key={metric.label}
              className="flex justify-between items-center"
            >
              <Typography className="text-gray-500 text-sm">
                {metric.label}
              </Typography>
              <Typography className="text-sm font-medium text-right max-w-[60%] line-clamp-1 flex items-center gap-1">
                {metric.value}
              </Typography>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-100">
          <button
            onClick={() => navigate(`/project/${project?.id || project?._id}`)}
            className="text-red-600 font-medium flex items-center hover:text-red-700 transition-colors bg-white/80 backdrop-blur-sm py-2 px-3 rounded-lg shadow-sm hover:shadow-md"
          >
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ProjectsList = ({ projects, onMenuClick }: ProjectsListProps) => {
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 2 }}>
        <Grid container spacing={3}>
          <AnimatePresence>
            {projects.map((project, index) => (
              <Grid item xs={12} md={6} lg={4} key={project._id || index}>
                <ProjectCard
                  project={project}
                  index={index}
                  onMenuClick={onMenuClick}
                />
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      </Box>
    </Container>
  );
};

const DeleteConfirmationDialog = ({
  showDeleteDialog,
  setShowDeleteDialog,
  selectedProject,
  deleteProject,
}: any) => {
  const reduxUser: any = useSelector((state: any) => state.user);

  return (
    <Dialog
      open={showDeleteDialog}
      onClose={() => setShowDeleteDialog(false)}
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "#EF4444",
          fontWeight: "600",
        }}
      >
        <AlertCircle />
        Delete Project
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ opacity: 0.8 }}>
          Are you sure you want to delete "
          {selectedProject?.basicCompanyInfo?.companyName}"? This action cannot
          be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          onClick={() => setShowDeleteDialog(false)}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: "500",
            padding: "8px 16px",
          }}
        >
          Cancel
        </Button>
        <Button
          sx={{
            bgcolor: "#EF4444",
            "&:hover": { bgcolor: "#DC2626" },
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: "500",
            padding: "8px 16px",
            boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.2)",
          }}
          variant="contained"
          onClick={() => deleteProject(selectedProject?.id, reduxUser.id)}
        >
          Delete Project
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Projects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const reduxUser: any = useSelector((state: any) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<
    SearchState<Project> | any
  >(InitialSearchStates.projects);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const { dialogProps, makeRequest: makeSubscriptionGatedRequest } =
    useSubscriptionLimit(() => {
      navigate(settingsPath, {
        replace: true,
      });
    });

  const handleSearchProjects = useCallback(
    async (searchParams: any, metadata?: any) => {
      try {
        setIsLoading(true);
        if (config.inProd) {
          // Production API call
          const projectService = new ProjectService();
          const response = await projectService.getProjects({
            // Required user ID
            userId: metadata.userId,

            // Main query
            companyName: searchParams.query,

            // Pagination
            page: searchParams.pagination.currentPage,
            limit: searchParams.pagination.limit,

            // Sorting
            sortBy: searchParams.sorting.sortBy,
            sortOrder: searchParams.sorting.sortOrder === -1 ? "desc" : "asc",

            // Filters
            growthStage: searchParams.filters.growthStage,
            productStage: searchParams.filters.productStage,
            dateFrom: searchParams.filters.dateFrom,
            dateTo: searchParams.filters.dateTo,
          });
          setSearchResults((prevState: any) => ({
            ...prevState,
            data: response.projects,
            pagination: response.pagination,
            sorting: response.sorting,
          }));
        } else {
          // Mock data handling
          setSearchResults((prevState: any) => ({
            ...prevState,
            sorting: searchParams.sorting,
          }));
        }
      } catch (error: Error | any) {
        console.error("Search failed:", error);
        displayToastMessage(
          "error",
          "Failed to search projects. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const handleInitialLoad = useCallback(async () => {
    try {
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        navigate(homeRoutePath, {
          replace: true,
        });
        return;
      }

      const sessionService = new SessionService();
      const sessionData = await sessionService.reviewSession();
      if (!sessionData.success) {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      dispatch(setUser(sessionData.userSessionData));

      await handleSearchProjects(InitialSearchStates.projects, {
        userId: sessionData.userSessionData.id,
      });
    } catch (error: any) {
      logIssueNetworkRequestError("Projects.handleInitialLoad", error);
      navigate(homeRoutePath, { replace: true });
    }
  }, [dispatch, location, navigate, handleSearchProjects]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleMenuClick = (event: any, project: any) => {
    dispatch(setProject(project));
    setSelectedProject(project);
    setAnchorEl(event.currentTarget);
  };

  const createNewProject = useCallback(
    async (projectData: any) => {
      try {
        displayToastMessage("info", "Creating project...");

        const projectService = new ProjectService();
        await makeSubscriptionGatedRequest({
          requestFn: () => projectService.createProject(projectData),
          onSuccess: (_) => {
            setShowNewProjectDialog(false);
            displayToastMessage(
              "success",
              "Project created successfully! Please refresh."
            );
            (window as any).location.reload();
          },
          onError: (error) => {
            console.error("Failed to create project:", error);
            displayToastMessage(
              "error",
              `Failed to create project. Please try again. Error: ${error}`
            );
          },
        });
      } catch (error: Error | any) {
        console.error("Failed to create project:", error);
        displayToastMessage(
          "error",
          `Failed to create project. Please try again. Error: ${error}`
        );
      }
    },
    [makeSubscriptionGatedRequest]
  );

  const deleteProject = useCallback(async (id: string, userId: string) => {
    try {
      const projectService = new ProjectService();
      await projectService.deleteProject(id, userId);
      setShowDeleteDialog(false);
      displayToastMessage("success", "Project deleted successfully");
      (window as any).location.reload();
    } catch (error: Error | any) {
      console.error("Failed to delete project:", error);
      displayToastMessage(
        "error",
        `Failed to delete project. Please try again. Error: ${error}`
      );
    }
  }, []);

  return (
    <CommonLayout>
      <Box
        sx={{
          p: 3,
          background: "linear-gradient(to bottom, #f9fafb, #ffffff)",
          borderRadius: "12px",
          minHeight: "calc(100vh - 80px)",
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              mb: 4,
              padding: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(8px)",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: "1px solid rgba(229, 231, 235, 0.8)",
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.75rem", md: "2rem" },
                background: "linear-gradient(to right, #1f2937, #4b5563)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Projects
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <StyledButton
                variant="contained"
                startIcon={<Plus />}
                onClick={() => setShowNewProjectDialog(true)}
                sx={{
                  background: "linear-gradient(to right, #ef4444, #dc2626)",
                  boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.2)",
                  "&:hover": {
                    boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.3)",
                  },
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  padding: "8px 16px",
                }}
              >
                New Project
              </StyledButton>
            </Box>
          </Box>

          {/* SearchAndFilters Component */}
          <div className="mb-6">
            <SearchAndFilters
              totalShown={searchResults.data.length}
              onSearch={handleSearchProjects}
              pagination={searchResults.pagination}
              sorting={searchResults.sorting}
              filters={searchResults.filters}
              filterConfig={filterConfigs.projects}
              isLoading={isLoading}
            />
          </div>

          {/* Projects Grid + Loading Skeleton */}
          <SearchContentContainer
            isLoading={isLoading}
            data={searchResults.data}
            LoadingComponent={LoadingSkeleton}
            ContentComponent={ProjectsList}
            componentProps={{
              projects: searchResults.data,
              onMenuClick: handleMenuClick,
            }}
            loadingText="Loading projects..."
            emptyTitle="No Projects Found"
            emptyMessage="Try adjusting your search or filter criteria"
          />

          {/* Project Actions Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "10px",
                boxShadow:
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                "& .MuiMenuItem-root": {
                  py: 1,
                  px: 2,
                  my: 0.5,
                  mx: 1,
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "rgba(243, 244, 246, 0.8)",
                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                navigate(`/project/${selectedProject?.id}`);
                setAnchorEl(null);
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Settings size={16} />
                <Typography>Settings</Typography>
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowDeleteDialog(true);
                setAnchorEl(null);
              }}
              sx={{ color: "#EF4444" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Trash2 size={16} />
                <Typography>Delete</Typography>
              </Box>
            </MenuItem>
          </Menu>

          {/* New Project Dialog */}
          <NewProjectDialog
            userId={reduxUser.id}
            open={showNewProjectDialog}
            onClose={() => setShowNewProjectDialog(false)}
            onSubmit={createNewProject}
          />

          {/* Delete Confirmation Dialog */}
          <DeleteConfirmationDialog
            showDeleteDialog={showDeleteDialog}
            setShowDeleteDialog={setShowDeleteDialog}
            selectedProject={selectedProject}
            deleteProject={deleteProject}
          />

          {/* Subscription Limit Dialog */}
          <SubscriptionLimitDialog {...dialogProps} />
        </motion.div>
      </Box>
    </CommonLayout>
  );
};

export default Projects;
